/**
 * PayLinksCard — Abby Pay payment-link minting for a single match.
 *
 * Permission gate: `matches.create` (renders nothing if missing) — the
 * same high-trust key the backend route enforces, so an admin never
 * sees a button that is guaranteed to 403.
 *
 * Backend contract (backend/src/routes/admin-pay-links.ts):
 *   POST /v1/admin/matches/:matchId/pay-links
 *     200 { links: [{ user_id, url, expires_at }] }   — one per participant
 *     400 non-mutual match (message carries current status)
 *     403 missing admin permission
 *     503 portal flag off / [STORE-1] parity attestation unset / secret unavailable
 *
 * Tokens are STATELESS (signed JWTs): re-clicking re-mints fresh links;
 * previously issued links remain valid until their own expiry.
 *
 * States by match status:
 *   - mutual     → "Generate pay links" button (+ results table after mint)
 *   - connected  → informational: both sides already paid, nothing to mint
 *   - other      → informational: minting only possible while mutual
 *
 * Design: Abby Pay portal design 2026-07-18 §4 (admin route) / §8 (Nate flow).
 */
import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { toast } from '@/hooks/useToast';

interface UserRefShape {
  id: string;
  email: string | null;
  display_name: string | null;
}

interface MatchShape {
  match_id: string;
  status: string;
  user1: UserRefShape;
  user2: UserRefShape;
}

interface MintedPayLink {
  user_id: string;
  url: string;
  expires_at: string;
}

interface Props {
  match: MatchShape;
}

export function PayLinksCard({ match }: Props) {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [links, setLinks] = useState<MintedPayLink[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!hasPermission('matches.create')) return null;

  const isMutual = match.status === 'mutual';
  const isConnected = match.status === 'connected';

  const participantLabel = (userId: string): string => {
    const u =
      match.user1.id === userId
        ? match.user1
        : match.user2.id === userId
          ? match.user2
          : null;
    if (!u) return userId.slice(0, 8);
    return u.display_name || u.email || u.id.slice(0, 8);
  };

  const mint = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await adminFetch(
        `${apiUrl}/v1/admin/matches/${match.match_id}/pay-links`,
        { method: 'POST' },
      );
      const json = (await r.json()) as { links: MintedPayLink[] };
      setLinks(json.links);
      toast({
        title: 'Pay links minted',
        description: `${json.links.length} link(s) generated for this match.`,
        variant: 'success',
      });
    } catch (e: unknown) {
      setErr(payLinkErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = (url: string, label: string) => {
    navigator.clipboard?.writeText(url);
    toast({ title: 'Link copied', description: `Pay link for ${label}` });
  };

  return (
    <section className="mt-4 rounded-md border bg-card p-3 text-card-foreground">
      <h3 style={{ margin: '0 0 8px 0', fontSize: 15 }}>
        Abby Pay — payment links
      </h3>

      {isConnected && (
        <p className="text-[13px] text-muted-foreground">
          This match is already <strong>connected</strong> — both sides have
          completed the commitment step, so there is nothing to pay and no
          links can be minted.
        </p>
      )}

      {!isConnected && !isMutual && (
        <p className="text-[13px] text-muted-foreground">
          Pay links can only be minted while the match is{' '}
          <strong>mutual</strong> (current status:{' '}
          <code>{match.status}</code>).
        </p>
      )}

      {isMutual && (
        <>
          <p className="mb-2 text-[13px] text-muted-foreground">
            Mints one signed portal link per participant. Links are
            re-mintable — generating again issues fresh tokens, and any
            previously issued links stay valid until their own expiry.
          </p>
          <button
            onClick={mint}
            disabled={busy}
            type="button"
            className={inlineButtonClass}
          >
            {busy ? 'Minting…' : links ? 'Re-generate pay links' : 'Generate pay links'}
          </button>
        </>
      )}

      {err && <div className={errClass}>{err}</div>}

      {links && links.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '10px 0 0 0', padding: 0 }}>
          {links.map((link, i) => {
            const label = participantLabel(link.user_id);
            return (
              <li
                key={link.user_id}
                className={i === 0 ? '' : 'border-t'}
                style={{ padding: '8px 0', fontSize: 12 }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{label}</span>
                  <span className="text-muted-foreground">
                    expires {new Date(link.expires_at).toLocaleString()}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  <code
                    className="flex-1 overflow-x-auto whitespace-nowrap rounded bg-muted px-1.5 py-1 text-[11px]"
                    data-testid={`pay-link-url-${link.user_id}`}
                  >
                    {link.url}
                  </code>
                  <button
                    onClick={() => copy(link.url, label)}
                    type="button"
                    className={inlineButtonClass}
                  >
                    Copy
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * The backend serializes thrown route errors as RFC-7807 problem+json
 * ({ type, title, status, detail }) where `title` carries the route's
 * message verbatim (utils/errors.ts::handleError → createErrorResponse).
 * adminFetch only lifts a `message` field, so for admin errors
 * `e.message` degrades to "HTTP 503" — recover the verbatim server copy
 * from the attached body instead, with a clear fallback for 503.
 */
function payLinkErrorMessage(e: unknown): string {
  if (e instanceof AdminApiError) {
    const body = e.body;
    if (body && typeof body === 'object') {
      const b = body as { title?: unknown; message?: unknown; detail?: unknown };
      if (typeof b.message === 'string' && b.message) return b.message;
      if (typeof b.title === 'string' && b.title) return b.title;
      if (typeof b.detail === 'string' && b.detail) return b.detail;
    }
    if (e.status === 503) {
      return 'Abby Pay portal is not enabled in this environment (flag off or store-parity attestation unset).';
    }
    return e.message;
  }
  return 'Failed to mint pay links';
}

const inlineButtonClass =
  'cursor-pointer rounded border bg-background px-2.5 py-1.5 text-[13px] hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60';

const errClass = 'mt-1.5 text-xs text-destructive';
